import React, { useState } from 'react';

import Image from 'next/image';

import { usePostHog } from 'posthog-js/react';

import {
  Form,
  Button,
  TextFieldControlled as Input,
  SelectFieldNativeControlled as Select,
  ToggleFieldControlled as Toggle,
  Paragraph,
} from '@leafwell/components';

import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useWordPressContext } from 'contexts/wordpress-provider';

const IndustryList = [
  'Alternative Medicine',
  'Cannabis',
  'Health and Wellness',
  'Legislation',
  'Lifestyle',
  'Media',
  'Politics',
  'Research',
  'Tech',
  'Other',
].map(industry => {
  return {
    label: industry,
    value: industry,
  };
});

const OnMediaForm: React.FC<{
  showCloseButton?: boolean;
  onCloseButton?: () => void;
}> = ({ showCloseButton = false, onCloseButton }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const posthog = usePostHog();
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser } = useBrazeContext();
  const { translations } = useWordPressContext();

  async function handleSubmit({
    accepted_terms_of_service,
    email,
    name,
    industry,
    message,
  }) {
    if (accepted_terms_of_service) {
      // https://posthog.com/docs/product-analytics/identify#alias-assigning-multiple-distinct-ids-to-the-same-user
      posthog.alias(email);

      saveBrazeUser({
        name,
        email,
      });

      trackEvent({
        event: 'form_submit',
        form_name: `formType_${window?.location?.pathname}`,
        form_destination: window?.location?.pathname,
        form_submit_text: translations['Get in touch'],
        formType: 'media',
        email,
        name,
        industry,
        message,
      });

      setFormSubmitted(true);
    }
  }

  return (
    <div
      id="contact-us-media-features-form"
      className="relative grid p-10 gap-y-4 bg-lilac-50 rounded-lg max-w-lg justify-self-end"
    >
      <Paragraph
        className={[
          showCloseButton ? 'mt-4' : '',
          'text-center md:text-left max-w-md text-lg',
        ].join(' ')}
      >
        Fill out this quick form and we’ll get the process started for you.
      </Paragraph>
      <Form
        className="gap-y-4"
        mode="onSubmit"
        defaultValues={{
          email: '',
          name: '',
          industry: '',
          message: '',
        }}
        onSubmit={handleSubmit}
      >
        {formSubmitted ? (
          <p className="text-center">
            Thank you for your enquiry.
            <br />
            One of our team will be in touch with you as soon as possible.
          </p>
        ) : (
          <>
            <Input
              className="rounded-lg"
              type="text"
              name="name"
              placeholder={`${translations['Name']} *`}
              validations={['required']}
            />
            <Input
              className="rounded-lg"
              type="email"
              name="email"
              placeholder={`${translations['Email']} *`}
              validations={['required', 'email']}
            />
            <Select
              aria-label="Select the type of industry you are interested in writing about"
              name="industry"
              placeholder="Select the type of industry *"
              options={IndustryList}
              className="rounded-lg"
              validations={['required']}
            />
            <Input
              className="rounded-lg"
              type="textarea"
              name="message"
              placeholder="Message (optional)"
            />
            <Toggle
              className="border-gray-300"
              type="checkbox"
              name="accepted_terms_of_service"
              validations={['required']}
              options={[
                {
                  label: (
                    <p className="text-sm">
                      {translations['I have read and accept the']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/terms-of-use"
                      >
                        {translations['terms of service']}
                      </a>{' '}
                      {translations['and']}{' '}
                      <a
                        target="_blank"
                        href="https://leafwell.com/privacy-policy"
                      >
                        {translations['privacy policy']}
                      </a>
                    </p>
                  ),
                  value: '1',
                },
              ]}
            />
            <Button
              type="submit"
              label={translations['Get in touch']}
              name="submit"
              variant="secondary"
              filled={true}
              className="w-full rounded-lg mt-8"
            />
          </>
        )}
      </Form>
      {showCloseButton ? (
        <button
          className="absolute top-0 right-0 -translate-x-1/2	translate-y-1/2 w-8"
          onClick={() => {
            onCloseButton();
          }}
        >
          <Image
            src="/stop-button-dark.svg"
            alt="close button"
            width={52}
            height={52}
          />
        </button>
      ) : null}
    </div>
  );
};

export default OnMediaForm;
